import NiceModal, { useModal } from '@ebay/nice-modal-react';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';

import { useCallback, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { useDispatch } from 'react-redux';

import Button from 'components/field/Button';
import IntegerInput from 'components/field/IntegerInput';
import StringInput from 'components/field/StringInput';
import Form from 'components/form/Form';
import FormItem from 'components/form/FormItem';
import Modal from 'components/global/Modal';
import Typography from 'components/global/Typography';
import { ModalActions, ModalContent, ModalTitle } from 'components/global/styled/Modal.styled';
import { updateDynamicListData } from 'components/list/dynamicListSlice';

import useAlert from 'hooks/useAlert';
import useAxiosPrivate from 'hooks/useAxiosPrivate';
import useModalRouter from 'hooks/useModalRouter';
import { usePrompt } from 'hooks/usePrompt';

import { OTO_AI_CLIENT } from 'services/consts';

import AppLogger from 'utils/AppLogger';
import { DYNAMICLISTS } from 'utils/constants';

const OTOAIClientConfigurationForm = NiceModal.create(({ id, modalName, mode }) => {
	const modal = useModal();

	const dispatch = useDispatch();
	const axiosPrivate = useAxiosPrivate();
	const { showError, showSuccess } = useAlert();
	const { closeModal } = useModalRouter();

	const isViewMode = useMemo(() => mode === 'view', [mode]);

	const schema = useMemo(
		() =>
			yup.object().shape({
				query: yup.string().required(),
				queryKey: yup.string().required(),
				model: yup.string().required(),
				scheduleGroupAmount: yup.number().nullable()
			}),
		[]
	);

	const fillForm = async () => {
		const defaultValues = {
			query: null,
			queryKey: null,
			model: null,
			scheduleGroupAmount: null
		};

		if (!id) {
			return defaultValues;
		}
		try {
			const { data, status } = await axiosPrivate.post(OTO_AI_CLIENT.GET_OTO_AI_SERVICE_BY_ID, { id });
			if (status === 200) {
				return {
					...defaultValues,
					...data
				};
			} else {
				AppLogger.error(data);
				showError();
				closeModal();
			}
		} catch (error) {
			AppLogger.error(error);
			showError();
			return defaultValues;
		}
	};

	const formMethods = useForm({
		resolver: yupResolver(schema),
		defaultValues: fillForm
	});

	const {
		handleSubmit,
		formState: { isDirty, isSubmitSuccessful, isSubmitting, isLoading: isFormLoading }
	} = formMethods;

	usePrompt(isDirty && !isSubmitSuccessful && !isSubmitting);
	const isLoading = isFormLoading || isSubmitting;

	const handleCloseModal = useCallback(() => {
		if (modalName) {
			closeModal();
		} else {
			modal.remove();
		}
	}, [modalName]);

	const onSubmit = (data) => {
		const apiPath = mode === 'edit' ? OTO_AI_CLIENT.UPDATE_OTO_AI_SERVICE : OTO_AI_CLIENT.ADD_OTO_AI_SERVICE;
		axiosPrivate
			.post(apiPath, {
				...data,
				id
			})
			.then((response) => {
				const { status } = response;

				if (status === 200) {
					dispatch(updateDynamicListData(DYNAMICLISTS.OTO_AI_CLIENT_CONFIGURATION));
					showSuccess();
					handleCloseModal();
				}
			})
			.catch((error) => {
				AppLogger.error(error);
				showError({
					message: error.response?.data?.code || error.response?.data?.message
				});
			});
	};

	const modalTitle = useMemo(() => {
		switch (mode) {
			case 'edit':
				return 'editOTOAIClientConfiguration';
			case 'view':
				return 'viewOTOAIClientConfiguration';
			default:
				return 'addOTOAIClientConfiguration';
		}
	}, [mode]);

	return (
		<Modal fullwidth name={modalName} maxWidth="x-large">
			<ModalTitle>
				<Typography variant="p1">{modalTitle}</Typography>
			</ModalTitle>
			<ModalContent>
				<Form formMethods={formMethods}>
					<FormItem label="query" required>
						<StringInput name="query" disabled={isViewMode} />
					</FormItem>
					<FormItem label="queryKey" required>
						<StringInput name="queryKey" disabled={isViewMode} />
					</FormItem>
					<FormItem label="model" required>
						<StringInput name="model" disabled={isViewMode} />
					</FormItem>
					<FormItem label="scheduleGroupAmount" required>
						<IntegerInput name="scheduleGroupAmount" disabled={isViewMode} />
					</FormItem>
				</Form>
			</ModalContent>

			<ModalActions>
				<Button label={isViewMode ? 'close' : 'cancel'} variant="outline" onClick={handleCloseModal} />
				{!isViewMode && (
					<Button
						loading={isLoading}
						label={mode === 'edit' ? 'save' : 'button.add'}
						disabled={!isDirty}
						onClick={handleSubmit(onSubmit, (errors) => {
							AppLogger.log(errors);
						})}
					/>
				)}
			</ModalActions>
		</Modal>
	);
});

export default OTOAIClientConfigurationForm;
